import React, {useEffect, useRef, useState} from 'react';
import {Button, Card, Divider, Modal, Spin, message} from 'antd';
import {IoArrowBack} from 'react-icons/io5';
import {IoMdAdd} from 'react-icons/io';
import {CloseOutlined, PlusCircleOutlined, ThunderboltOutlined} from '@ant-design/icons';
import {useNavigate, useParams} from 'react-router-dom';
import {AnimatePresence, motion} from 'framer-motion';
import {getAllAnalysis, getPatientFilesList, postWirnklesAnalysis, postLessionDetector, getLessionDetector, saveImageAnalysis} from '../../../utils/api-lists';
import LoadingSpinner from '../../../components/AntCostomComponents/LoadingSpinner';
import AnalysisResultsComponent from '../pages/ai-analysis/analysis_results.component';
import AcneDetectionOverlay from './components/AcneDetectionOverlay.component';
import FaceAnalyzingAnimComponent from '../../../components/FaceAnalyzingAnim/face-analyzing-anim.component';
import toast from 'react-hot-toast';
import {useTranslation} from 'react-i18next';
import './image-comparison.css';

const {Meta} = Card;

const ImageComparisionPage = () => {
    const navigate = useNavigate();
    const {patientId} = useParams();
    const {t} = useTranslation("common");
    const [images, setImages] = useState([null, null]); // Two slots for comparison
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null); // Which slot to fill (0 or 1)
    const [availableImages, setAvailableImages] = useState([]);
    const [imagesWithoutAnalysis, setImagesWithoutAnalysis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState(null); // 'wrinkles' or 'acne' - applies to both slots
    const [acneDetailMode, setAcneDetailMode] = useState(false); // Show detailed view with popovers for acne
    const [isExpanded, setIsExpanded] = useState(false); // For floating button expansion
    
    // Analysis states
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analyzingImageId, setAnalyzingImageId] = useState(null);
    const [currentAnalysisProcess, setCurrentAnalysisProcess] = useState(null);

    // Refs and overlay box per slot
    const containerRefs = [useRef(null), useRef(null)];
    const imageRefs = [useRef(null), useRef(null)];
    const [overlayBoxes, setOverlayBoxes] = useState([null, null]);

    // Helper to compute the rendered image box inside a contain-fitted image
    const computeOverlayBox = (slotIndex) => {
        const container = containerRefs[slotIndex]?.current;
        const img = imageRefs[slotIndex]?.current;
        if (!container || !img) return null;

        const containerRect = container.getBoundingClientRect();
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        if (!naturalWidth || !naturalHeight) return null;

        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;
        const containerRatio = containerWidth / containerHeight;
        const imageRatio = naturalWidth / naturalHeight;

        let renderedWidth, renderedHeight, offsetLeft, offsetTop;

        if (imageRatio > containerRatio) {
            // Image constrained by width
            renderedWidth = containerWidth;
            renderedHeight = containerWidth / imageRatio;
            offsetLeft = 0;
            offsetTop = (containerHeight - renderedHeight) / 2;
        } else {
            // Image constrained by height
            renderedHeight = containerHeight;
            renderedWidth = containerHeight * imageRatio;
            offsetTop = 0;
            offsetLeft = (containerWidth - renderedWidth) / 2;
        }

        return {
            renderedWidth,
            renderedHeight,
            offsetLeft,
            offsetTop
        };
    };

    // Recalculate overlay boxes when images load or window resizes
    useEffect(() => {
        const handleResize = () => {
            setOverlayBoxes(prev => [
                computeOverlayBox(0) || prev[0],
                computeOverlayBox(1) || prev[1]
            ]);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleImageLoaded = (slotIndex) => {
        setOverlayBoxes(prev => {
            const next = [...prev];
            next[slotIndex] = computeOverlayBox(slotIndex) || null;
            return next;
        });
    };

    // Fetch available analysis images
    const fetchAnalysisImages = async () => {
        setLoading(true);
        try {
            const [filesResponse, analysisResponse] = await Promise.all([
                getPatientFilesList(patientId),
                getAllAnalysis(patientId)
            ]);

            if (filesResponse.data && analysisResponse.data) {
                // Filter to only include image files (exclude videos like webm)
                const imageFiles = filesResponse.data.filter(file => file.type === 'image');

                // Group analyses by image ID
                const groupedData = groupAnalysisByImageId(analysisResponse.data, imageFiles);
                setAvailableImages(groupedData);

                // Find images without any analysis
                const imageIdsWithAnalysis = new Set(
                    analysisResponse.data.map(analysis => analysis.image_id)
                );

                const imagesWithoutAnalysis = imageFiles
                    .filter(file => !imageIdsWithAnalysis.has(file.id))
                    .map(file => ({
                        image_id: file.id,
                        image_url: file.url,
                        image_name: file.image_name,
                        date: new Date(file.created_at || file.upload_date),
                        analysis_count: 0,
                        analyses: []
                    }))
                    .sort((a, b) => b.date - a.date);

                setImagesWithoutAnalysis(imagesWithoutAnalysis);
            }
        } catch (err) {
            console.error("Error fetching analysis data:", err);
        }
        setLoading(false);
    };

    const groupAnalysisByImageId = (analysisFiles, patientFiles) => {
        const validAnalyses = analysisFiles.filter(analysis =>
            analysis.files && Object.keys(analysis.files).length > 0
        );

        const patientFilesMap = patientFiles.reduce((acc, file) => {
            acc[file.id] = file;
            return acc;
        }, {});

        const groupedAnalyses = validAnalyses.reduce((groups, analysis) => {
            const imageId = analysis.image_id;
            const targetGroupId = patientFilesMap[imageId] ? imageId : `standalone-${analysis.id}`;

            if (!groups[targetGroupId]) {
                const patientFile = patientFilesMap[imageId];
                let imageUrl = "";
                let imageName = "Analysis Image";

                if (patientFile) {
                    imageUrl = patientFile.url;
                    imageName = patientFile.image_name;
                } else if (analysis.files.original_image) {
                    imageUrl = analysis.files.original_image.jpg || analysis.files.original_image.png;
                }

                groups[targetGroupId] = {
                    image_id: imageId,
                    analysis_count: 0,
                    image_url: imageUrl,
                    image_name: imageName,
                    date: new Date(analysis.created_at),
                    analyses: []
                };
            } else {
                const analysisDate = new Date(analysis.created_at);
                if (analysisDate > groups[targetGroupId].date) {
                    groups[targetGroupId].date = analysisDate;
                }
            }

            groups[targetGroupId].analysis_count++;

            const processedAnalysis = {
                id: analysis.id,
                analysis_type: analysis.analysis_type,
                created_at: analysis.created_at
            };

            // Extract file URLs based on analysis type
            if (analysis.analysis_type === 1) { // Wrinkle detection
                processedAnalysis.data_url = analysis.files['grading_results.json'];
                processedAnalysis.original_image_url = analysis.files['original_image.png'];
                processedAnalysis.visualization_url = analysis.files['visualization.png'];
                processedAnalysis.wrinkle_mask_url = analysis.files['wrinkle_mask.png'];
            } else if (analysis.analysis_type === 2) { // Acne detection
                processedAnalysis.data_url = analysis.files['detections.json'];
                processedAnalysis.original_image_url = analysis.files['original_image.jpg'];
                processedAnalysis.visualization_url = analysis.files['visualization.jpg'];
            }

            groups[targetGroupId].analyses.push(processedAnalysis);

            return groups;
        }, {});

        return Object.values(groupedAnalyses).sort((a, b) => b.date - a.date);
    };

    useEffect(() => {
        if (patientId) {
            fetchAnalysisImages();
        }
    }, [patientId]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleStartNewAnalysis = () => {
        window.location.reload();
    };

    const toggleHighlight = (type) => {
        // Toggle the filter - if clicking same type, turn off, otherwise switch to new type
        setActiveFilter(prev => prev === type ? null : type);

        // Reset acne detail mode when switching away from acne
        if (type === 'wrinkles') {
            setAcneDetailMode(false);
        }
    };

    const toggleAcneDetailMode = () => {
        setAcneDetailMode(prev => !prev);
    };

    const handleEmptySlotClick = (slotIndex) => {
        setSelectedSlot(slotIndex);
        setIsModalOpen(true);
    };

    const handleImageSelect = async (selectedImage) => {
        // Check if image has analysis
        if (selectedImage.analysis_count === 0) {
            // No analysis - start analysis automatically
            setIsModalOpen(false);
            await handleAnalyzeImageAndAdd(selectedImage, selectedSlot);
        } else {
            // Has analysis - add directly to comparison
            const newImages = [...images];
            newImages[selectedSlot] = {
                id: selectedImage.image_id,
                url: selectedImage.image_url,
                name: selectedImage.image_name,
                date: selectedImage.date.toLocaleDateString(),
                analysisData: selectedImage // Store full data for analysis viewing
            };
            setImages(newImages);
            setIsModalOpen(false);
        }
    };

    const handleRemoveImage = (slotIndex) => {
        const newImages = [...images];
        newImages[slotIndex] = null;
        setImages(newImages);
    };

    // Helper function to convert image to base64
    const convertImageToBase64 = (imageFile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(imageFile);
            reader.onloadend = () => {
                resolve(reader.result.split(',')[1]);
            };
            reader.onerror = (error) => {
                reject(error);
            };
        });
    };

    // Extract UUID from URL
    const extractUuidFromUrl = (url) => {
        const uuidPattern = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i;
        const match = url.match(uuidPattern);
        return match ? match[1] : null;
    };

    const extractUuidFromUrlAcne = (url) => {
        return url.split('/').pop();
    };

    // Perform analysis on an image and add to comparison slot
    const handleAnalyzeImageAndAdd = async (imageData, slotIndex) => {
        if (isAnalyzing) return;

        setIsAnalyzing(true);
        setAnalyzingImageId(imageData.image_id);

        try {
            // Fetch the image
            const fetchImageResponse = await fetch(imageData.image_url, {
                headers: {
                    'Access-Control-Allow-Origin': '*'
                }
            });
            const imageBlob = await fetchImageResponse.blob();
            const imageFile = new File([imageBlob], imageData.image_name, {
                type: fetchImageResponse.headers.get('content-type') || 'image/jpeg'
            });

            // Perform wrinkles analysis
            setCurrentAnalysisProcess('wrinkles');
            const base64Image = await convertImageToBase64(imageFile);
            
            const wrinklesResponse = await toast.promise(
                postWirnklesAnalysis(base64Image),
                {
                    loading: t('analyzing_wrinkles') || 'Analyzing wrinkles...',
                    success: t('wrinkles_analysis_complete') || 'Wrinkles analysis complete',
                    error: (e) => `${t('analysis_failed') || 'Analysis failed'}: ${e.response?.data?.message || t('unknown_error') || 'Unknown error'}`
                }
            );

            const wrinklesAnalysisId = wrinklesResponse.data?.analysis_id ?? extractUuidFromUrl(wrinklesResponse.data?.cloud_upload?.cloud_url ?? '');

            if (wrinklesAnalysisId && process.env.REACT_APP_ENV !== 'development') {
                await saveImageAnalysis({
                    patient_id: patientId,
                    analysis_id: wrinklesAnalysisId,
                    image_id: imageData.image_id,
                });
            }

            // Perform acne/lesion analysis
            setCurrentAnalysisProcess('skin_lesions');
            
            const detectResponse = await postLessionDetector(base64Image);
            let acneResponse = {};
            
            if (detectResponse.data?.task_id) {
                const taskId = detectResponse.data?.task_id;
                let i = 1;
                do {
                    await new Promise(resolve => setTimeout(resolve, i * 1000));
                    acneResponse = await getLessionDetector(taskId);
                    i += 1;
                } while (acneResponse.data?.state === 'STARTED');
            }

            acneResponse = {
                data: acneResponse.data.result
            };

            const acneAnalysisId = acneResponse.data?.analysis_id ?? extractUuidFromUrlAcne(acneResponse.data?.cloud_upload?.folder ?? '');

            if (acneAnalysisId && window.location.hostname !== 'localhost') {
                await toast.promise(
                    saveImageAnalysis({
                        patient_id: patientId,
                        analysis_id: acneAnalysisId,
                        analysis_type: 2,
                        image_id: imageData.image_id,
                    }),
                    {
                        loading: t('saving_analysis') || 'Saving analysis...',
                        success: t('analysis_saved') || 'Analysis saved',
                        error: (e) => `${t('save_failed') || 'Save failed'}`
                    }
                );
            }

            toast.success(t('analysis_complete') || 'Analysis complete!');
            
            // Refresh the analysis data
            await fetchAnalysisImages();
            
            // Find the updated image data with analysis
            const [filesResponse, analysisResponse] = await Promise.all([
                getPatientFilesList(patientId),
                getAllAnalysis(patientId)
            ]);

            if (filesResponse.data && analysisResponse.data) {
                // Filter to only include image files (exclude videos like webm)
                const imageFiles = filesResponse.data.filter(file => file.type === 'image');

                const groupedData = groupAnalysisByImageId(analysisResponse.data, imageFiles);
                const updatedImageData = groupedData.find(img => img.image_id === imageData.image_id);
                
                if (updatedImageData) {
                    // Add the analyzed image to the comparison slot
                    const newImages = [...images];
                    newImages[slotIndex] = {
                        id: updatedImageData.image_id,
                        url: updatedImageData.image_url,
                        name: updatedImageData.image_name,
                        date: updatedImageData.date.toLocaleDateString(),
                        analysisData: updatedImageData
                    };
                    setImages(newImages);
                }
            }
            
        } catch (error) {
            console.error('Error performing analysis:', error);
            toast.error(t('analysis_failed') || 'Analysis failed. Please try again.');
        } finally {
            setIsAnalyzing(false);
            setAnalyzingImageId(null);
            setCurrentAnalysisProcess(null);
        }
    };

    // Perform analysis on an image (for standalone analysis button)
    const handleAnalyzeImage = async (imageData) => {
        if (isAnalyzing) return;

        setIsAnalyzing(true);
        setAnalyzingImageId(imageData.image_id);
        setIsModalOpen(false);

        try {
            // Fetch the image
            const fetchImageResponse = await fetch(imageData.image_url, {
                headers: {
                    'Access-Control-Allow-Origin': '*'
                }
            });
            const imageBlob = await fetchImageResponse.blob();
            const imageFile = new File([imageBlob], imageData.image_name, {
                type: fetchImageResponse.headers.get('content-type') || 'image/jpeg'
            });

            // Perform wrinkles analysis
            setCurrentAnalysisProcess('wrinkles');
            const base64Image = await convertImageToBase64(imageFile);
            
            const wrinklesResponse = await toast.promise(
                postWirnklesAnalysis(base64Image),
                {
                    loading: t('analyzing_wrinkles') || 'Analyzing wrinkles...',
                    success: t('wrinkles_analysis_complete') || 'Wrinkles analysis complete',
                    error: (e) => `${t('analysis_failed') || 'Analysis failed'}: ${e.response?.data?.message || t('unknown_error') || 'Unknown error'}`
                }
            );

            const wrinklesAnalysisId = wrinklesResponse.data?.analysis_id ?? extractUuidFromUrl(wrinklesResponse.data?.cloud_upload?.cloud_url ?? '');

            if (wrinklesAnalysisId && process.env.REACT_APP_ENV !== 'development') {
                await saveImageAnalysis({
                    patient_id: patientId,
                    analysis_id: wrinklesAnalysisId,
                    image_id: imageData.image_id,
                });
            }

            // Perform acne/lesion analysis
            setCurrentAnalysisProcess('skin_lesions');
            
            const detectResponse = await postLessionDetector(base64Image);
            let acneResponse = {};
            
            if (detectResponse.data?.task_id) {
                const taskId = detectResponse.data?.task_id;
                let i = 1;
                do {
                    await new Promise(resolve => setTimeout(resolve, i * 1000));
                    acneResponse = await getLessionDetector(taskId);
                    i += 1;
                } while (acneResponse.data?.state === 'STARTED');
            }

            acneResponse = {
                data: acneResponse.data.result
            };

            const acneAnalysisId = acneResponse.data?.analysis_id ?? extractUuidFromUrlAcne(acneResponse.data?.cloud_upload?.folder ?? '');

            if (acneAnalysisId && window.location.hostname !== 'localhost') {
                await toast.promise(
                    saveImageAnalysis({
                        patient_id: patientId,
                        analysis_id: acneAnalysisId,
                        analysis_type: 2,
                        image_id: imageData.image_id,
                    }),
                    {
                        loading: t('saving_analysis') || 'Saving analysis...',
                        success: t('analysis_saved') || 'Analysis saved',
                        error: (e) => `${t('save_failed') || 'Save failed'}`
                    }
                );
            }

            toast.success(t('analysis_complete') || 'Analysis complete! Refreshing data...');
            
            // Refresh the analysis data
            await fetchAnalysisImages();
            
        } catch (error) {
            console.error('Error performing analysis:', error);
            toast.error(t('analysis_failed') || 'Analysis failed. Please try again.');
        } finally {
            setIsAnalyzing(false);
            setAnalyzingImageId(null);
            setCurrentAnalysisProcess(null);
        }
    };

    return (
        <div className="image-comparison-page">
            {/* Header */}
            <div className="comparison-header">
                <div className="header-left">
                    <button className="back-button" onClick={handleBack}>
                        <IoArrowBack size={20}/>
                        <span>Back to Patient</span>
                    </button>
                </div>

                <div className="header-right">
                    <Button
                        type="primary"
                        className="start-analysis-btn"
                        icon={<IoMdAdd size={18}/>}
                        onClick={handleStartNewAnalysis}
                    >
                        Start New Analysis
                    </Button>
                </div>
            </div>

            {/* Loading State */}
            {loading && <LoadingSpinner/>}

            {/* Image Grid */}
            {!loading && (
                <div className="images-grid">
                    {images.map((image, index) => (
                        <div key={index} className="image-card-wrapper">
                            <div className="image-card">
                                {image ? (
                                    // Filled slot
                                    <>
                                        <div
                                            className="image-container-with-overlay"
                                            ref={containerRefs[index]}
                                        >
                                            <img
                                                ref={imageRefs[index]}
                                                src={image.url}
                                                alt={image.name}
                                                className="patient-image"
                                                style={{
                                                    opacity: activeFilter === 'acne' ? 0 : 1,
                                                    transition: 'opacity 0.3s ease'
                                                }}
                                                onLoad={() => handleImageLoaded(index)}
                                            />

                                            {/* Wrinkle Overlay */}
                                            {activeFilter === 'wrinkles' && overlayBoxes[index] && image.analysisData.analyses
                                                .filter(a => a.analysis_type === 1)
                                                .map((wrinkle, idx) => (
                                                    wrinkle.wrinkle_mask_url && (
                                                        <div
                                                            key={idx}
                                                            className="analysis-overlay wrinkle-overlay"
                                                            style={{
                                                                backgroundImage: `url(${wrinkle.wrinkle_mask_url})`,
                                                                width: overlayBoxes[index].renderedWidth,
                                                                height: overlayBoxes[index].renderedHeight,
                                                                left: overlayBoxes[index].offsetLeft,
                                                                top: overlayBoxes[index].offsetTop
                                                            }}
                                                        />
                                                    )
                                                ))
                                            }

                                            {/* Acne Overlay */}
                                            {activeFilter === 'acne' && overlayBoxes[index] && (
                                                acneDetailMode ? (
                                                    // Detailed view with popovers
                                                    <div
                                                        className="analysis-overlay acne-overlay-detailed"
                                                        style={{
                                                            width: overlayBoxes[index].renderedWidth,
                                                            height: overlayBoxes[index].renderedHeight,
                                                            left: overlayBoxes[index].offsetLeft,
                                                            top: overlayBoxes[index].offsetTop
                                                        }}
                                                    >
                                                        <AcneDetectionOverlay
                                                            acneAnalysis={image.analysisData.analyses.find(a => a.analysis_type === 2)}
                                                            originalImageUrl={image.url}
                                                        />
                                                    </div>
                                                ) : (
                                                    // Full visualization view
                                                    image.analysisData.analyses
                                                        .filter(a => a.analysis_type === 2)
                                                        .map((acne, idx) => (
                                                            acne.visualization_url && (
                                                                <img
                                                                    key={idx}
                                                                    src={acne.visualization_url}
                                                                    alt="Acne visualization"
                                                                    className="analysis-overlay acne-overlay-full"
                                                                    style={{
                                                                        width: overlayBoxes[index].renderedWidth,
                                                                        height: overlayBoxes[index].renderedHeight,
                                                                        left: overlayBoxes[index].offsetLeft,
                                                                        top: overlayBoxes[index].offsetTop
                                                                    }}
                                                                />
                                                            )
                                                        ))
                                                )
                                            )}

                                            {/* Hover Overlay */}
                                            <div className="image-overlay">
                                                <div className="overlay-top">
                                                    <button
                                                        className="zoom-btn remove-btn"
                                                        onClick={() => handleRemoveImage(index)}
                                                        title="Remove image"
                                                    >
                                                        <CloseOutlined/>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>




                                    </>
                                ) : (
                                    // Empty slot with placeholder
                                    <div
                                        className="image-container empty-slot"
                                        onClick={() => handleEmptySlotClick(index)}
                                    >
                                        <div className="empty-slot-content">
                                            <PlusCircleOutlined className="plus-icon"/>
                                            <p>Add Image</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Analysis Details under image */}
                            {image && (
                                <div className="analysis-results-wrapper">
                                    <AnalysisResultsComponent
                                        analysis={image.analysisData}
                                        hideImages={true}
                                        hideTabs={true}
                                        activeAnalysisType={activeFilter === 'wrinkles' ? 1 : activeFilter === 'acne' ? 2 : null}
                                        hideProgressBars={true}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal for selecting images */}
            <Modal
                title="Select Analysis Image"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={900}
                className="image-selection-modal"
            >
                <div className="modal-image-grid">
                    {availableImages.length > 0 || imagesWithoutAnalysis.length > 0 ? (
                        <>
                            {/* Images with analysis */}
                            {availableImages.length > 0 && (
                                <>
                                    <h3 style={{gridColumn: '1 / -1', marginBottom: '16px', color: '#1890ff'}}>
                                        {t("images_with_analysis") || "Images with Analysis"}
                                    </h3>
                                    {availableImages.map((imageData) => (
                                        <Card
                                            key={imageData.image_id}
                                            hoverable
                                            onClick={() => handleImageSelect(imageData)}
                                            cover={
                                                <div style={{overflow: "hidden", height: "300px"}}>
                                                    <img
                                                        alt={imageData.image_name}
                                                        src={imageData.image_url}
                                                        style={{height: '100%', objectFit: 'cover', width: '100%'}}
                                                    />
                                                </div>
                                            }
                                        >
                                            <Meta
                                                title={imageData.image_name}
                                                description={
                                                    <>
                                                        <div>{imageData.date.toLocaleDateString()}</div>
                                                        <div style={{color: '#52c41a', fontWeight: 500}}>
                                                            ✓ {imageData.analysis_count} Analysis Available
                                                        </div>
                                                    </>
                                                }
                                            />
                                        </Card>
                                    ))}
                                </>
                            )}

                            {/* Divider if both sections have content */}
                            {availableImages.length > 0 && imagesWithoutAnalysis.length > 0 && (
                                <Divider style={{gridColumn: '1 / -1', margin: '24px 0'}}/>
                            )}

                            {/* Images without analysis */}
                            {imagesWithoutAnalysis.length > 0 && (
                                <>
                                    <h3 style={{gridColumn: '1 / -1', marginBottom: '16px', color: '#faad14'}}>
                                        {t("images_without_analysis") || "Images without Analysis"}
                                    </h3>
                                    {imagesWithoutAnalysis.map((imageData) => (
                                        <Card
                                            key={imageData.image_id}
                                            hoverable
                                            className="unanalyzed-card"
                                            cover={
                                                <div style={{overflow: "hidden", height: "300px", position: 'relative'}}>
                                                    <img
                                                        alt={imageData.image_name}
                                                        src={imageData.image_url}
                                                        style={{height: '100%', objectFit: 'cover', width: '100%'}}
                                                    />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        background: 'rgba(0, 0, 0, 0.5)',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '12px',
                                                        opacity: 0,
                                                        transition: 'opacity 0.3s ease'
                                                    }}
                                                    className="card-hover-overlay"
                                                    >
                                                        <div style={{
                                                            color: 'white',
                                                            fontSize: '14px',
                                                            textAlign: 'center',
                                                            padding: '0 20px',
                                                            fontWeight: 500
                                                        }}>
                                                            {t("click_to_analyze_and_add") || "Click to analyze and add to comparison"}
                                                        </div>
                                                        <ThunderboltOutlined style={{
                                                            fontSize: '32px',
                                                            color: '#667eea'
                                                        }} />
                                                    </div>
                                                </div>
                                            }
                                            onClick={() => handleImageSelect(imageData)}
                                        >
                                            <Meta
                                                title={imageData.image_name}
                                                description={
                                                    <>
                                                        <div>{imageData.date.toLocaleDateString()}</div>
                                                        <div style={{color: '#faad14', fontWeight: 500, marginTop: '8px'}}>
                                                            ⚡ {t("will_analyze_on_select") || "Will analyze on select"}
                                                        </div>
                                                    </>
                                                }
                                            />
                                        </Card>
                                    ))}
                                </>
                            )}
                        </>
                    ) : (
                        <div className="no-images-message">
                            <p>{t("no_images_available") || "No images available"}</p>
                            <Button
                                type="primary"
                                icon={<IoMdAdd/>}
                                onClick={handleStartNewAnalysis}
                            >
                                {t("start_new_analysis") || "Start New Analysis"}
                            </Button>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Floating Filter Controls */}
            <motion.div
                className="floating-controls"
                onHoverStart={() => setIsExpanded(true)}
                onHoverEnd={() => setIsExpanded(false)}
                initial={false}
            >
                {/* Filter options - expand on hover */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            className="filter-options"
                            initial={{opacity: 0, y: 20}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: 20}}
                            transition={{duration: 0.2}}
                        >
                            <motion.div
                                initial={{opacity: 0, x: 20}}
                                animate={{opacity: 1, x: 0}}
                                transition={{delay: 0.05}}
                            >
                                <Button
                                    type={activeFilter === 'wrinkles' ? "primary" : "default"}
                                    onClick={() => toggleHighlight('wrinkles')}
                                    disabled={!images.some(img => img && img.analysisData.analyses.some(a => a.analysis_type === 1))}
                                    className="filter-option-btn"
                                >
                                    {activeFilter === 'wrinkles' ? '✓ ' : ''}Wrinkles
                                </Button>
                            </motion.div>

                            <motion.div
                                initial={{opacity: 0, x: 20}}
                                animate={{opacity: 1, x: 0}}
                                transition={{delay: 0.1}}
                            >
                                <Button
                                    type={activeFilter === 'acne' ? "primary" : "default"}
                                    onClick={() => toggleHighlight('acne')}
                                    disabled={!images.some(img => img && img.analysisData.analyses.some(a => a.analysis_type === 2))}
                                    className="filter-option-btn"
                                >
                                    {activeFilter === 'acne' ? '✓ ' : ''}Acne
                                </Button>
                            </motion.div>

                            {/* Acne Detail Mode Toggle - only show when acne is active */}
                            {activeFilter === 'acne' && images.some(img => img && img.analysisData.analyses.some(a => a.analysis_type === 2)) && (
                                <motion.div
                                    initial={{opacity: 0, x: 20}}
                                    animate={{opacity: 1, x: 0}}
                                    transition={{delay: 0.15}}
                                >
                                    <Button
                                        type={acneDetailMode ? "primary" : "default"}
                                        onClick={toggleAcneDetailMode}
                                        className="filter-option-btn"
                                        size="small"
                                    >
                                        {acneDetailMode ? '✓ ' : ''}Detailed View
                                    </Button>
                                </motion.div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Main floating button - hide when expanded */}
                <motion.div
                    className="main-float-btn"
                    initial={{opacity: 1, scale: 1}}
                    animate={{
                        opacity: isExpanded ? 0 : 1,
                        scale: isExpanded ? 0.8 : 1
                    }}
                    transition={{duration: 0.2}}
                >
                    <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        className="filter-toggle-btn"
                    >
                        🔍
                    </Button>
                </motion.div>
            </motion.div>

            {/* Analysis Loading Modal */}
            <Modal
                title={t("analyzing_image") || "Analyzing Image"}
                open={isAnalyzing}
                footer={null}
                closable={false}
                width="90%"
                style={{ maxWidth: '600px', top: '5vh' }}
                bodyStyle={{ padding: '16px', maxHeight: '80vh', overflowY: 'auto' }}
                centered={false}
            >
                <div>
                    {(imagesWithoutAnalysis.find(img => img.image_id === analyzingImageId) || 
                      availableImages.find(img => img.image_id === analyzingImageId)) && (
                        <FaceAnalyzingAnimComponent
                            image_url={
                                imagesWithoutAnalysis.find(img => img.image_id === analyzingImageId)?.image_url || 
                                availableImages.find(img => img.image_id === analyzingImageId)?.image_url
                            }
                            process_name={currentAnalysisProcess}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ImageComparisionPage;

